import {
    generatePath,
    Navigate,
    Outlet,
    useNavigate,
    useOutletContext,
    useParams,
} from 'react-router-dom';
import {
    RequestMethod,
    useServerFetch,
    useServerSuccessEffect,
    useServerWrite,
} from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import {
    ApprovingContractInfo,
    BarrierGateIdentificationEntity,
    BarrierGateIdentificationTicket,
    OffstreetTransactionActions,
    PassageInfo,
} from './OperatorOffstreetTransactionTypes.ts';
import { OperatorAsyncLoadedSection } from '../app/components/OperatorAsyncLoadedSection.tsx';
import { OperatorRoutedModalContent } from '../layout/components/OperatorRoutedModalContent.tsx';
import { HeaderComponent } from '../layout/components/HeaderComponent.tsx';
import { Localized, useLocalize } from 'dg-web-shared/lib/Localization.tsx';
import { HeaderActionCloseButtons } from '../layout/components/HeaderActionCloseButtons.tsx';
import {
    ApprovingContractInfoField,
    BarrierGateIdentificationEnityValue,
    NoReceiptForRunningContract,
    OffstreetTransactionActionText,
    OffstreetTransactionOpen,
    OffstreetTransactionPassageText,
    OffstreetTransactionRefundBlocked,
    OffstreetTransactionTransit,
    OperatorOffstreetTransactionDetailRoutedModal,
} from './OperatorOffstreetTransactionShared.tsx';
import { Box, Stack } from '@mui/material';
import { DateTime } from 'luxon';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { IdentificationType } from '../common/components/IdentificationItems.tsx';
import {
    ConfirmationDialog,
    ConfirmationDialogVersions,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { type JSX, useState } from 'react';
import { TerminalProvider } from '../common/state/useParkingsFetch.ts';
import { ReadOnlyTextField } from '../ui/material-ui/ReadOnlyTextField.tsx';
import { PaymentChannel } from '../common/models/PaymentChannel.ts';
import {
    TicketState,
    useOffstreetTransactionsOutlet,
} from './OperatorOffstreetTransactionsRoute.tsx';
import {
    BlockLabel,
    FormContentLayout,
    FormLeftColumn,
    FormRightColumn,
} from '../layout/components/form/FormLayout.tsx';
import {
    OffstreetTransactionPayStationPayments,
    PaystationPaymentData,
    PendingTicketPriceDisplay,
} from './OperatorOffstreetTransactionTicket.tsx';
import { RecodeErrorType } from '../cloud-connector/CloudConnectorRecodeEntryWithoutExitConditionsOutlet.tsx';
import { useOperatorContext } from '../app/components/BaseLayoutAndData.tsx';
import { useQuery } from '../shared-mui-components/filter/OperatorFilterHelpers.tsx';
import { CloudConnectorRecodeErrorText } from '../cloud-connector/CloudConnectorRecodeResponse.tsx';
import { Message } from 'dg-web-shared/lib/Localized.ts';
import { PaymentChannelLocalized } from '../common/components/PaymentChannelLocalized.tsx';
import { TransactionRefundBlockedExplanation } from '../common/models/OperatorRefund.ts';
import { openNewTab } from 'dg-web-shared/common/utils/Navigation.tsx';
import { urlWithOperatorExternalId } from '../utils/Utils.ts';
import { EntryResolvedReason } from '../cloud-connector/CloudConnectorPeripheriesOverview.tsx';
import { Formatter } from 'dg-web-shared/lib/Date.ts';

interface EntryWithoutExitVehicle {
    barrierGateProvider: TerminalProvider | null;
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    resolvedAt: string | null;
}

export interface PoolFullInfo {
    poolFullContractId: number;
    poolFullContractExternalId: string;
    poolFullPermitTypeName: string;
    poolFullEntryWithoutExitVehicles: EntryWithoutExitVehicle[];
}

interface EntryWithoutExitCancellationInfo {
    resolvedAt: string;
    resolveReason: EntryResolvedReason;
    operatorLoginName: string | null;
}

const enum BarrierGateAntipassbackType {
    DISABLED = 'disabled',
    AUTOCLEARING_24H = 'autoclearing_24h',
    AUTOCLEARING_12H = 'autoclearing_12h',
    MANUAL_CLEARING = 'manual_clearing',
}

const enum EntryWithoutExitDetailType {
    REDIRECT_TO_TICKET = 'REDIRECT_TO_TICKET',
    REDIRECT_TO_CONTRACT = 'REDIRECT_TO_CONTRACT',
    TICKET = 'TICKET',
    NON_TICKET = 'NON_TICKET',
}

type EntryWithoutExitDetails =
    | EntryWithoutExitDetailsTicketRedirect
    | EntryWithoutExitDetailsContractRedirect
    | EntryWithoutExitDetailsNonTicket
    | EntryWithoutExitDetailsTicket;

type EntryWithoutExitDetailsTicketRedirect = {
    entryWithoutExitDetailType: EntryWithoutExitDetailType.REDIRECT_TO_TICKET;
    ticketId: string;
};

type EntryWithoutExitDetailsContractRedirect = {
    entryWithoutExitDetailType: EntryWithoutExitDetailType.REDIRECT_TO_CONTRACT;
    contractExternalId: string;
};

type EntryWithoutExitDetailsBase = {
    entryWithoutExitDetailType: EntryWithoutExitDetailType;
    barrierGateIdentificationEntity: BarrierGateIdentificationEntity;
    barrierGateProvider: TerminalProvider | null;
    refundBlockedExplanation: TransactionRefundBlockedExplanation;
    zoneName: string;
    entryInfo: PassageInfo;
    transitsInfo: PassageInfo[] | null;
    entryWithoutExitId: number;
    displayLpn: string | null;
    paystationPayments: PaystationPaymentData[] | null;
    entryWithoutExitRecodeError: RecodeErrorType | null;
    recodeConditionDescriptions: Message[];
    entryNameIfZoneHasMultiple: string | null;
    cancellationInfo: EntryWithoutExitCancellationInfo | null;
};

type EntryWithoutExitDetailsNonTicket = EntryWithoutExitDetailsBase & {
    entryWithoutExitDetailType: EntryWithoutExitDetailType.NON_TICKET;
    antipassbackType: BarrierGateAntipassbackType;
    antipassbackActive: boolean;
    approvingContractInfo: ApprovingContractInfo | null;
    poolFullInfo: PoolFullInfo | null;
    paymentChannel: PaymentChannel;
};

type EntryWithoutExitDetailsTicket = EntryWithoutExitDetailsBase & {
    entryWithoutExitDetailType: EntryWithoutExitDetailType.TICKET;
    barrierGateIdentificationEntity: BarrierGateIdentificationTicket;
    barrierGateProvider: TerminalProvider.CLOUD_CONNECTOR;
    ticketState: TicketState;
    showGuestPermitGeneration: boolean;
    unapprovedAt: string | null;
};

export function OperatorOffstreetTransactionEntryWithoutExit() {
    const urlParams = useParams<{ entryWithoutExitId: string }>();
    const query = useQuery();
    const { refetchList } = useOffstreetTransactionsOutlet();
    const entryWithoutExitId = urlParams.entryWithoutExitId
        ? parseInt(urlParams.entryWithoutExitId, 10)
        : null;
    const [offstreetTransactionsState, refetchOffstreetTransaction] =
        useServerFetch<
            EntryWithoutExitDetails,
            { entryWithoutExitId: number },
            null
        >(
            context => ({
                url: `/ui-api/operator-account/offstreet-transactions/entry-without-exit/${context.entryWithoutExitId}`,
            }),
            entryWithoutExitId ? { entryWithoutExitId } : null,
        );

    return (
        <OperatorOffstreetTransactionDetailRoutedModal
            render={controller => (
                <>
                    <OperatorAsyncLoadedSection
                        requestState={offstreetTransactionsState}
                        render={transaction => {
                            switch (transaction.entryWithoutExitDetailType) {
                                case EntryWithoutExitDetailType.NON_TICKET:
                                    return (
                                        <TransactionEntryWithoutExitDetailContent
                                            transaction={transaction}
                                            onClose={controller.close}
                                            refetchList={refetchList}
                                            refetchOffstreetTransaction={
                                                refetchOffstreetTransaction
                                            }
                                        />
                                    );
                                case EntryWithoutExitDetailType.TICKET:
                                    return (
                                        <EntryWithoutExitTrxTicketDetailContent
                                            transaction={transaction}
                                            onClose={controller.close}
                                        />
                                    );
                                case EntryWithoutExitDetailType.REDIRECT_TO_TICKET:
                                    return (
                                        <Navigate
                                            to={`${generatePath(
                                                `../ticket/:ticketId/detail`,
                                                {
                                                    ticketId:
                                                        transaction.ticketId,
                                                },
                                            )}?${query}`}
                                        />
                                    );
                                case EntryWithoutExitDetailType.REDIRECT_TO_CONTRACT:
                                    return (
                                        <Navigate
                                            to={`${generatePath(
                                                `../contract/:contractExternalId/detail`,
                                                {
                                                    contractExternalId:
                                                        transaction.contractExternalId,
                                                },
                                            )}?${query}`}
                                        />
                                    );
                            }
                        }}
                    />

                    <OffstreetTransactionEntryWithouExitOutlet
                        context={{
                            refetchEntryWithoutExitDetailDesktop:
                                refetchOffstreetTransaction,
                            refetchList: refetchList,
                        }}
                    />
                </>
            )}
        />
    );
}

interface CancelEntryPayload {
    entryWithoutExitId: number;
}

function TransactionEntryWithoutExitDetailContent({
    transaction,
    onClose,
    refetchList,
    refetchOffstreetTransaction,
}: {
    transaction: EntryWithoutExitDetailsNonTicket;
    onClose: () => void;
    refetchList: () => void;
    refetchOffstreetTransaction: () => void;
}) {
    const [cancelEntryState, cancelEntry] = useServerWrite<
        CancelEntryPayload,
        null
    >(() => ({
        url: `/ui-api/operator-account/offstreet-transaction/entry/cancel`,
        method: RequestMethod.DELETE,
    }));
    const [cancelEntryDialogOpen, setCancelEntryDialogOpen] = useState(false);
    useServerSuccessEffect(cancelEntryState, () => {
        refetchList();
        onClose();
    });

    const [disableAntipassbackState, disableAntipassback] = useServerWrite<
        { entryWithoutExitId: number },
        null
    >(({ entryWithoutExitId }) => ({
        url: `/ui-api/operator-account/offstreet-transaction/entry/${entryWithoutExitId}/disable-antipassback`,
    }));
    const [disableAntipassbackDialogOpen, setDisableAntipassbackDialogOpen] =
        useState(false);
    useServerSuccessEffect(disableAntipassbackState, () => {
        refetchList();
        refetchOffstreetTransaction();
    });

    const navigate = useNavigate();
    const localize = useLocalize();
    const {
        currentLogin: { permissions },
        operatorExternalId,
    } = useOperatorContext();
    const query = useQuery();
    const recodeDisabled =
        !permissions.cloudConnectorRecodeTicket ||
        transaction.entryWithoutExitRecodeError != null;
    const hasPaystationPayments =
        transaction.paystationPayments &&
        transaction.paystationPayments.length > 0;

    function cancelEntryOrDisableAntipassbackAction() {
        if (
            transaction.barrierGateProvider !== TerminalProvider.CLOUD_CONNECTOR
        ) {
            return {
                key: 'cancel-entry',
                label: (
                    <OffstreetTransactionActionText
                        action={OffstreetTransactionActions.CANCEL_ENTRY}
                    />
                ),
                onClickCallback: () => setCancelEntryDialogOpen(true),
                disabled: false,
            };
        }

        return {
            key: 'disable-antipassback',
            label: (
                <OffstreetTransactionActionText
                    action={OffstreetTransactionActions.DISABLE_ANTIPASSBACK}
                />
            ),
            onClickCallback: () => setDisableAntipassbackDialogOpen(true),
            disabled: !transaction.antipassbackActive,
            tooltip: !transaction.antipassbackActive ? (
                <AntipassbackAlreadyDisabled />
            ) : undefined,
        };
    }

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Parkvorgang"
                            fr="Procédure"
                            it="Procedura"
                            en="Procedure"
                        />
                    }
                    editMode={false}
                    buttons={
                        <HeaderActionCloseButtons
                            onClose={onClose}
                            dropdownItems={
                                !transaction.cancellationInfo
                                    ? [
                                          cancelEntryOrDisableAntipassbackAction(),
                                          {
                                              key: 'recode',
                                              label: (
                                                  <OffstreetTransactionActionText
                                                      action={
                                                          OffstreetTransactionActions.RECODE
                                                      }
                                                  />
                                              ),
                                              onClickCallback: () =>
                                                  navigate(
                                                      `${generatePath(
                                                          'recode',
                                                      )}?${query}`,
                                                  ),
                                              disabled: recodeDisabled,
                                              tooltip: recodeDisabled ? (
                                                  <RunningTicketTooltip
                                                      recodePermission={
                                                          permissions.cloudConnectorRecodeTicket
                                                      }
                                                      recodeError={
                                                          transaction.entryWithoutExitRecodeError
                                                      }
                                                  />
                                              ) : undefined,
                                          },
                                          {
                                              key: 'reimburse',
                                              label: (
                                                  <OffstreetTransactionActionText
                                                      action={
                                                          OffstreetTransactionActions.REIMBURSE_CUSTOMER
                                                      }
                                                  />
                                              ),
                                              onClickCallback: () => {},
                                              disabled: true,
                                              tooltip: (
                                                  <OffstreetTransactionRefundBlocked
                                                      refundBlockedExplanation={
                                                          transaction.refundBlockedExplanation
                                                      }
                                                  />
                                              ),
                                          },
                                          {
                                              key: 'download-receipt',
                                              label: (
                                                  <OffstreetTransactionActionText
                                                      action={
                                                          OffstreetTransactionActions.DOWNLOAD_RECEIPT
                                                      }
                                                  />
                                              ),
                                              onClickCallback: () => {
                                                  if (hasPaystationPayments) {
                                                      openNewTab(
                                                          urlWithOperatorExternalId(
                                                              `/ui-api/operator-account/pay-station-payments-by-entry-without-exit/${transaction.entryWithoutExitId}/receipt`,
                                                              operatorExternalId,
                                                          ),
                                                      );
                                                  }
                                              },
                                              disabled: !hasPaystationPayments,
                                              tooltip:
                                                  !hasPaystationPayments ? (
                                                      <NoReceiptForRunningContract />
                                                  ) : undefined,
                                          },
                                      ]
                                    : undefined
                            }
                        />
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <CancelEntryConfirmationDialog
                            onConfirm={() =>
                                cancelEntry({
                                    entryWithoutExitId:
                                        transaction.entryWithoutExitId,
                                })
                            }
                            onCancel={() => setCancelEntryDialogOpen(false)}
                            open={cancelEntryDialogOpen}
                        />
                        <DisableAntipassbackConfirmationDialog
                            onConfirm={() =>
                                disableAntipassback({
                                    entryWithoutExitId:
                                        transaction.entryWithoutExitId,
                                })
                            }
                            onCancel={() =>
                                setDisableAntipassbackDialogOpen(false)
                            }
                            open={disableAntipassbackDialogOpen}
                        />
                        <OffstreetTransactionOpen
                            isPresent={true}
                            hasAntipassback={transaction.antipassbackActive}
                            displayLpn={transaction.displayLpn}
                            isCancelled={transaction.cancellationInfo != null}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={
                                <BarrierGateIdentificationEnityValue
                                    barrierGateIdentificationEntity={
                                        transaction.barrierGateIdentificationEntity
                                    }
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Einfahrt"
                                    fr="Entrée"
                                    it="Entrata"
                                    en="Entry"
                                />
                            }
                            value={
                                <OffstreetTransactionPassageText
                                    passageInfo={transaction.entryInfo}
                                />
                            }
                        />
                        <OffstreetTransactionTransit
                            transitsInfo={transaction.transitsInfo}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ausfahrt"
                                    fr="Sortie"
                                    it="Uscita"
                                    en="Exit"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Parking"
                                    fr="Parking"
                                    it="Parcheggio"
                                    en="Parking"
                                />
                            }
                            value={transaction.zoneName}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={EmDash}
                        />
                        {transaction.recodeConditionDescriptions.length > 0 && (
                            <Stack
                                direction={'row'}
                                sx={{
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Rabatt"
                                                fr="Rabais"
                                                it="Sconto"
                                                en="Rebate"
                                            />
                                        }
                                        value={EmDash}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Bedingung"
                                                fr="Condition"
                                                it="Condizione"
                                                en="Condition"
                                            />
                                        }
                                        value={
                                            <Box
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {transaction.recodeConditionDescriptions
                                                    .map(localize)
                                                    .join('\n')}
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Stack>
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={
                                <PaymentChannelLocalized
                                    paymentChannel={transaction.paymentChannel}
                                />
                            }
                        />
                        {transaction.paystationPayments && (
                            <OffstreetTransactionPayStationPayments
                                paystationPayments={
                                    transaction.paystationPayments
                                }
                            />
                        )}
                        <ApprovingContractInfoField
                            approvingContractInfo={
                                transaction.approvingContractInfo
                            }
                            poolFullInfo={transaction.poolFullInfo}
                        />
                        {transaction.poolFullInfo && (
                            <PoolFullEntryWithoutExitVehicles
                                poolFullEntryWithoutExitVehicles={
                                    transaction.poolFullInfo
                                        .poolFullEntryWithoutExitVehicles
                                }
                            />
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Antipassback-Typ"
                                    fr="Type antipassback"
                                    it="Tipo antipassback"
                                    en="Antipassback type"
                                />
                            }
                            value={
                                <AntipassbackType
                                    barrierGateAntipassbackType={
                                        transaction.antipassbackType
                                    }
                                />
                            }
                        />
                        {transaction.cancellationInfo && (
                            <EntryWithoutExitCancellationInfo
                                cancellationInfo={transaction.cancellationInfo}
                            />
                        )}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'end',
                            }}
                        >
                            <BlockLabel
                                label={
                                    <Localized
                                        de="Erstellt am"
                                        fr="Créé le"
                                        it="Creato il"
                                        en="Created at"
                                    />
                                }
                            >
                                {EmDash}
                            </BlockLabel>
                            <BlockLabel label="ID">{EmDash}</BlockLabel>
                        </Box>
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

function EntryWithoutExitCancellationInfo({
    cancellationInfo,
}: {
    cancellationInfo: EntryWithoutExitCancellationInfo;
}) {
    return (
        <ReadOnlyTextField
            label={
                <Localized
                    de="Annullierung"
                    fr="Annulation"
                    it="Annullamento"
                    en="Cancellation"
                />
            }
            value={
                <Stack direction={'column'}>
                    <Box>
                        {Formatter.dayMonthYearHourMinute(
                            DateTime.fromISO(cancellationInfo.resolvedAt),
                        )}
                    </Box>
                    <Box>
                        <EntryWithoutExitResolvedTranslation
                            resolveReason={cancellationInfo.resolveReason}
                        />
                        {cancellationInfo.operatorLoginName &&
                            ` (${cancellationInfo.operatorLoginName})`}
                    </Box>
                </Stack>
            }
        />
    );
}

function EntryWithoutExitResolvedTranslation({
    resolveReason,
}: {
    resolveReason: EntryResolvedReason;
}) {
    switch (resolveReason) {
        case EntryResolvedReason.cleared_by_operator:
            return (
                <Localized
                    de="manuell"
                    fr="manuellement"
                    it="manualmente"
                    en="manually"
                />
            );
        case EntryResolvedReason.barrier_opened_by_operator:
            return (
                <Localized
                    de="manuelle Ausfahrt"
                    fr="sortie manuelle"
                    it="uscita manuale"
                    en="manual exit"
                />
            );
        case EntryResolvedReason.overwritten_by_entry:
            return (
                <Localized
                    de="durch neue Einfahrt"
                    fr="par nouvelle entrée"
                    it="per nuova entrata"
                    en="by new entry"
                />
            );
        case EntryResolvedReason.canceled_by_equipment:
            return (
                <Localized
                    de="durch lokale Anlage"
                    fr="par l'installation locale"
                    it="dall'impianto locale"
                    en="by local equipment"
                />
            );
        case EntryResolvedReason.regular_exit:
        case EntryResolvedReason.cleared_by_system:
            return <>{EmDash}</>;
    }
}

function EntryWithoutExitTrxTicketDetailContent({
    transaction,
    onClose,
}: {
    transaction: EntryWithoutExitDetailsTicket;
    onClose: () => void;
}) {
    const localize = useLocalize();
    const navigate = useNavigate();
    const { operatorExternalId } = useOperatorContext();
    const {
        currentLogin: { permissions },
    } = useOperatorContext();
    const query = useQuery();
    const recodeDisabled =
        !permissions.cloudConnectorRecodeTicket ||
        transaction.entryWithoutExitRecodeError != null;
    const hasPaystationPayments =
        transaction.paystationPayments &&
        transaction.paystationPayments.length > 0;

    return (
        <OperatorRoutedModalContent
            header={
                <HeaderComponent
                    title={
                        <Localized
                            de="Ticket"
                            fr="Ticket"
                            it="Ticket"
                            en="Ticket"
                        />
                    }
                    editMode={false}
                    buttons={
                        <HeaderActionCloseButtons
                            onClose={onClose}
                            dropdownItems={
                                !transaction.cancellationInfo
                                    ? [
                                          ...(transaction.showGuestPermitGeneration
                                              ? [
                                                    {
                                                        key: 'generate-guest-permit',
                                                        label: (
                                                            <OffstreetTransactionActionText
                                                                action={
                                                                    OffstreetTransactionActions.GENERATE_GUEST_PERMIT
                                                                }
                                                            />
                                                        ),
                                                        onClickCallback: () =>
                                                            navigate(
                                                                `${generatePath(
                                                                    `guest-permit`,
                                                                )}?${query}`,
                                                            ),
                                                        disabled: false,
                                                    },
                                                ]
                                              : []),
                                          {
                                              key: 'recode',
                                              label: (
                                                  <OffstreetTransactionActionText
                                                      action={
                                                          OffstreetTransactionActions.RECODE
                                                      }
                                                  />
                                              ),
                                              onClickCallback: () =>
                                                  navigate(
                                                      `${generatePath(
                                                          'recode',
                                                      )}?${query}`,
                                                  ),
                                              disabled: recodeDisabled,
                                              tooltip: recodeDisabled ? (
                                                  <RunningTicketTooltip
                                                      recodePermission={
                                                          permissions.cloudConnectorRecodeTicket
                                                      }
                                                      recodeError={
                                                          transaction.entryWithoutExitRecodeError
                                                      }
                                                  />
                                              ) : undefined,
                                          },
                                          {
                                              key: 'reimburse',
                                              label: (
                                                  <OffstreetTransactionActionText
                                                      action={
                                                          OffstreetTransactionActions.REIMBURSE_CUSTOMER
                                                      }
                                                  />
                                              ),
                                              onClickCallback: () => {},
                                              disabled: true,
                                              tooltip: (
                                                  <OffstreetTransactionRefundBlocked
                                                      refundBlockedExplanation={
                                                          transaction.refundBlockedExplanation
                                                      }
                                                  />
                                              ),
                                          },
                                          {
                                              key: 'download-receipt',
                                              label: (
                                                  <OffstreetTransactionActionText
                                                      action={
                                                          OffstreetTransactionActions.DOWNLOAD_RECEIPT
                                                      }
                                                  />
                                              ),
                                              onClickCallback: () => {
                                                  if (hasPaystationPayments) {
                                                      openNewTab(
                                                          urlWithOperatorExternalId(
                                                              `/ui-api/operator-account/pay-station-payments-by-entry-without-exit/${transaction.entryWithoutExitId}/receipt`,
                                                              operatorExternalId,
                                                          ),
                                                      );
                                                  }
                                              },
                                              disabled: !hasPaystationPayments,
                                              tooltip:
                                                  !hasPaystationPayments ? (
                                                      <NoReceiptForRunningContract />
                                                  ) : undefined,
                                          },
                                      ]
                                    : undefined
                            }
                        />
                    }
                />
            }
            body={
                <FormContentLayout>
                    <FormLeftColumn>
                        <OffstreetTransactionOpen
                            isPresent={true}
                            hasAntipassback={false}
                            displayLpn={transaction.displayLpn}
                            isCancelled={transaction.cancellationInfo != null}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Identifikation"
                                    fr="Identification"
                                    it="Identificazione"
                                    en="Identification"
                                />
                            }
                            value={
                                <BarrierGateIdentificationEnityValue
                                    barrierGateIdentificationEntity={
                                        transaction.barrierGateIdentificationEntity
                                    }
                                />
                            }
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Einfahrt"
                                    fr="Entrée"
                                    it="Entrata"
                                    en="Entry"
                                />
                            }
                            value={
                                <OffstreetTransactionPassageText
                                    passageInfo={transaction.entryInfo}
                                />
                            }
                        />
                        <OffstreetTransactionTransit
                            transitsInfo={transaction.transitsInfo}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Ausfahrt"
                                    fr="Sortie"
                                    it="Uscita"
                                    en="Exit"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Dauer"
                                    fr="Durée"
                                    it="Durata"
                                    en="Duration"
                                />
                            }
                            value={EmDash}
                        />
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Parking"
                                    fr="Parking"
                                    it="Parcheggio"
                                    en="Parking"
                                />
                            }
                            value={transaction.zoneName}
                        />
                    </FormLeftColumn>
                    <FormRightColumn>
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Bezahlt"
                                    fr="Payé"
                                    it="Pagato"
                                    en="Paid"
                                />
                            }
                            value={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '4px',
                                        alignItems: 'center',
                                    }}
                                >
                                    <PendingTicketPriceDisplay
                                        state={transaction.ticketState}
                                        unapprovedAt={transaction.unapprovedAt}
                                    />
                                </Box>
                            }
                        />
                        {transaction.recodeConditionDescriptions.length > 0 && (
                            <Stack
                                direction={'row'}
                                sx={{
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Rabatt"
                                                fr="Rabais"
                                                it="Sconto"
                                                en="Rebate"
                                            />
                                        }
                                        value={EmDash}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', flexGrow: 1 }}>
                                    <ReadOnlyTextField
                                        label={
                                            <Localized
                                                de="Bedingung"
                                                fr="Condition"
                                                it="Condizione"
                                                en="Condition"
                                            />
                                        }
                                        value={
                                            <Box
                                                sx={{
                                                    whiteSpace: 'pre-line',
                                                }}
                                            >
                                                {transaction.recodeConditionDescriptions
                                                    .map(localize)
                                                    .join('\n')}
                                            </Box>
                                        }
                                    />
                                </Box>
                            </Stack>
                        )}
                        <ReadOnlyTextField
                            label={
                                <Localized
                                    de="Kanal"
                                    fr="Canal"
                                    it="Canale"
                                    en="Channel"
                                />
                            }
                            value={
                                transaction.paystationPayments &&
                                transaction.paystationPayments.length > 0 ? (
                                    <PaymentChannelLocalized
                                        paymentChannel={
                                            PaymentChannel.PAY_STATION
                                        }
                                    />
                                ) : (
                                    EmDash
                                )
                            }
                        />
                        <OffstreetTransactionPayStationPayments
                            paystationPayments={transaction.paystationPayments}
                        />
                        {transaction.cancellationInfo && (
                            <EntryWithoutExitCancellationInfo
                                cancellationInfo={transaction.cancellationInfo}
                            />
                        )}
                        <Box
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'end',
                            }}
                        >
                            <BlockLabel
                                label={
                                    <Localized
                                        de="Erstellt am"
                                        fr="Créé le"
                                        it="Creato il"
                                        en="Created at"
                                    />
                                }
                            >
                                {EmDash}
                            </BlockLabel>
                            <BlockLabel label="ID">{EmDash}</BlockLabel>
                        </Box>
                    </FormRightColumn>
                </FormContentLayout>
            }
        />
    );
}

export function useOffstreetTransactionEntryWithoutExitOutlet() {
    return useOutletContext<OffstreetTransactionEntryWithoutExitOutletContext>();
}

export function OffstreetTransactionEntryWithouExitOutlet({
    context,
}: {
    context: OffstreetTransactionEntryWithoutExitOutletContext;
}) {
    return <Outlet context={context} />;
}

interface OffstreetTransactionEntryWithoutExitOutletContext {
    refetchEntryWithoutExitDetailDesktop: () => void;
    refetchList: () => void;
}

function PoolFullEntryWithoutExitVehicles({
    poolFullEntryWithoutExitVehicles,
}: {
    poolFullEntryWithoutExitVehicles: EntryWithoutExitVehicle[];
}) {
    if (poolFullEntryWithoutExitVehicles.length == 0) {
        return null;
    }
    const entity2Key = (entity: BarrierGateIdentificationEntity): string => {
        switch (entity.identificationMedia) {
            case IdentificationType.BADGE:
                return entity.labelNr;
            case IdentificationType.LICENSE_PLATE:
                return entity.licenseNumber;
            case IdentificationType.QR_CODE:
                return entity.qrCodeId;
            case IdentificationType.TICKET:
                return entity.ticket.ticketId;
        }
    };
    return (
        <ReadOnlyTextField
            label={
                <Localized
                    de="Anwesende Fahrzeuge"
                    fr="Véhicules présents"
                    it="Veicoli presenti"
                    en="Vehicles present"
                />
            }
            value={poolFullEntryWithoutExitVehicles.map(vehicle => (
                <Box
                    key={entity2Key(vehicle.barrierGateIdentificationEntity)}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                    }}
                >
                    <Box sx={{ marginRight: '35px' }}>
                        <BarrierGateIdentificationEnityValue
                            barrierGateIdentificationEntity={
                                vehicle.barrierGateIdentificationEntity
                            }
                            barrierGateProvider={vehicle.barrierGateProvider}
                        />
                    </Box>
                    {vehicle.resolvedAt ? (
                        <>
                            <Localized
                                de="(bis "
                                fr="(jusqu'au "
                                it="(fino al "
                                en="(until "
                            />
                            {DateTime.fromISO(vehicle.resolvedAt).toFormat(
                                'dd.MM.yyyy – HH:mm',
                            ) + ')'}
                        </>
                    ) : (
                        <Localized
                            de="(noch anwesend)"
                            fr="(toujours présent)"
                            it="(ancora presente)"
                            en="(still present)"
                        />
                    )}
                </Box>
            ))}
        />
    );
}

function AntipassbackType({
    barrierGateAntipassbackType,
}: {
    barrierGateAntipassbackType: BarrierGateAntipassbackType | null;
}): JSX.Element {
    switch (barrierGateAntipassbackType) {
        case BarrierGateAntipassbackType.DISABLED:
        case null:
            return <>{EmDash}</>;

        case BarrierGateAntipassbackType.AUTOCLEARING_24H:
            return (
                <Localized
                    de="aktiv in den ersten 24h"
                    fr="actif les premières 24h"
                    it="attivo le prime 24h"
                    en="active the first 24h"
                />
            );
        case BarrierGateAntipassbackType.AUTOCLEARING_12H:
            return (
                <Localized
                    de="aktiv in den ersten 12h"
                    fr="actif les premières 12h"
                    it="attivo le prime 12h"
                    en="active the first 12h"
                />
            );

        case BarrierGateAntipassbackType.MANUAL_CLEARING:
            return (
                <Localized
                    de="aktiv bis auf Widerruf"
                    fr="actif jusqu'à révocation"
                    it="attivo fino a revoca"
                    en="active until revoked"
                />
            );
    }
}

function CancelEntryConfirmationDialog({
    open,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Bestätigen"
                    fr="Confirmer"
                    it="Conferma"
                    en="Confirm"
                />
            }
            onConfirm={onConfirm}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onCancel={onCancel}
            title={
                <OffstreetTransactionActionText
                    action={OffstreetTransactionActions.CANCEL_ENTRY}
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie diese Einfahrt wirklich annulieren?"
                    fr="Voulez-vous vraiment supprimer cette entrée?"
                    it="Vuole veramente cancellare questa entrata?"
                    en="Do you really want to delete this entry?"
                />
            </p>
        </ConfirmationDialog>
    );
}

function DisableAntipassbackConfirmationDialog({
    open,
    onConfirm,
    onCancel,
}: {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}) {
    return (
        <ConfirmationDialog
            open={open}
            confirmLabel={
                <Localized
                    de="Deaktivieren"
                    fr="Désactiver"
                    it="Disattiva"
                    en="Deactivate"
                />
            }
            onConfirm={onConfirm}
            cancelLabel={
                <Localized
                    de="Abbrechen"
                    fr="Annuler"
                    it="Annulla"
                    en="Cancel"
                />
            }
            onCancel={onCancel}
            title={
                <OffstreetTransactionActionText
                    action={OffstreetTransactionActions.DISABLE_ANTIPASSBACK}
                />
            }
            dialogVersion={ConfirmationDialogVersions.HARD_WARNING}
        >
            <p>
                <Localized
                    de="Möchten Sie den Antipassback für diesen Parkvorgang wirklich deaktivieren?"
                    fr="Voulez-vous vraiment désactiver l'antipassback pour cette procédure?"
                    it="Vuole veramente disattivare l'anti-passback per questa procedura?"
                    en="Do you really want to deactivate the anti-passback for this park transaction?"
                />
            </p>
        </ConfirmationDialog>
    );
}

export function AntipassbackAlreadyDisabled() {
    return (
        <Localized
            de="Antipassback ist für diesen Parkvorgang bereits inaktiv"
            fr="L'anti-passback est déjà inactif pour cette procédure"
            it="L'anti-passback è già inattivo per questa procedura"
            en="Antipassback is already inactive for this park transaction"
        />
    );
}

function RunningTicketTooltip({
    recodePermission,
    recodeError,
}: {
    recodePermission: boolean;
    recodeError: RecodeErrorType | null;
}) {
    if (!recodePermission) {
        return <MissingPermission />;
    }
    if (recodeError != null) {
        return <CloudConnectorRecodeErrorText type={recodeError} />;
    }
    return null;
}

function MissingPermission() {
    return (
        <Localized
            de="Funktion nicht verfügbar: Ihnen fehlen die erforderlichen Berechtigungen"
            fr="Fonction non disponible: vous ne disposez pas des droits nécessaires"
            it="Funzione non disponibile: non dispone dei diritti necessari"
            en="Function not available: you don't have the necessary permissions"
        />
    );
}
